import { useState, useEffect } from "react";
import { message } from "antd";
import { departmentPerformanceService } from "Services/AdminServices/ReportServices/DepartmentPerformanceService";

function useDepartmentPerformance() {
  const [performanceData, setPerformanceData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  // Point 13: optional day/week/month period filter ("" = All Time).
  const [periodType, setPeriodType] = useState("");
  const [referenceDate, setReferenceDate] = useState(null);
  const [periodLabel, setPeriodLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchingPerformance, setFetchingPerformance] = useState(false);
  const [error, setError] = useState(null);

  // Fetch departments list on component mount
  useEffect(() => {
    const fetchDepartmentsList = async () => {
      try {
        setLoading(true);
        setError(null);
        const departmentsList = await departmentPerformanceService.getDepartmentList();
        
        // Handle different response structures
        let departmentArray = [];
        if (Array.isArray(departmentsList)) {
          departmentArray = departmentsList;
        } else if (departmentsList && departmentsList.data && Array.isArray(departmentsList.data)) {
          departmentArray = departmentsList.data;
        } else {
          throw new Error("Unexpected API response format for departments list");
        }
        
        setDepartments(departmentArray);
        
        // Select the first department by default if available
        if (departmentArray.length > 0) {
          // Try to find the department ID field (could be id, departmentId, etc.)
          const firstDept = departmentArray[0];
          const deptId = firstDept.id || firstDept.departmentId || firstDept.value;
          if (deptId) {
            setSelectedDepartmentId(deptId);
          }
        }
      } catch (err) {
        console.error("Failed to fetch departments:", err);
        setError("Failed to load departments. Please try again.");
        message.error("Failed to load departments");
      } finally {
        setLoading(false);
      }
    };

    fetchDepartmentsList();
  }, []);

  // Fetch performance data when a department is selected
  useEffect(() => {
    if (selectedDepartmentId) {
      const fetchPerformanceData = async () => {
        try {
          setFetchingPerformance(true);
          setError(null);
          const result = await departmentPerformanceService.getDepartmentPerformance(
            selectedDepartmentId,
            periodType,
            referenceDate
          );
          setPerformanceData(result.data);
          setPeriodLabel(result.periodLabel || "");
        } catch (err) {
          console.error("Failed to fetch department performance:", err);
          setError("Failed to load performance data. Please try again.");
          message.error("Failed to load performance data");
          setPerformanceData(null);
        } finally {
          setFetchingPerformance(false);
        }
      };

      fetchPerformanceData();
    }
  }, [selectedDepartmentId, periodType, referenceDate]);

  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartmentId(departmentId);
  };

  const handlePeriodTypeChange = (value) => setPeriodType(value);
  const handleReferenceDateChange = (value) => setReferenceDate(value);

  return {
    performanceData,
    departments,
    selectedDepartmentId,
    periodType,
    referenceDate,
    periodLabel,
    loading,
    fetchingPerformance,
    error,
    handleDepartmentChange,
    handlePeriodTypeChange,
    handleReferenceDateChange,
  };
}

export default useDepartmentPerformance;